"use client";

import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { ChevronDown, ListTree, X } from "lucide-react";
import { useTranslations } from "next-intl";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { renderBelowFoldBands } from "@/components/showcase/below-fold";

export type CoursePageSection = {
  id: string;
  label: string;
  fallbackIds?: string[];
};

type NavigatorProps = {
  sections: CoursePageSection[];
  /**
   * `SITE-CRS01`: the course page's sticky enroll bar is near-black and sits at
   * the bottom of the screen, so its trigger is drawn for a dark ground and the
   * panel opens upward. The defaults are the old light, downward look.
   */
  tone?: "light" | "dark";
  placement?: "down" | "up";
};

const SECTION_OBSERVER_ROOT_MARGIN = "-104px 0px -62% 0px";

function resolveSectionTarget(section: CoursePageSection): HTMLElement | null {
  const ids = [section.id, ...(section.fallbackIds ?? [])];
  for (const id of ids) {
    const target = document.getElementById(id);
    if (target) return target;
  }
  return null;
}

function useResolvedSections(sections: CoursePageSection[]) {
  const [resolvedIds, setResolvedIds] = useState<Record<string, string>>({});
  const sectionSignature = sections
    .map(
      (section) =>
        `${section.id}:${section.label}:${(section.fallbackIds ?? []).join(",")}`,
    )
    .join("|");

  useEffect(() => {
    const next: Record<string, string> = {};
    for (const section of sections) {
      const target = resolveSectionTarget(section);
      if (target) next[section.id] = target.id;
    }
    setResolvedIds(next);
  }, [sectionSignature]);

  return useMemo(
    () =>
      sections
        .map((section) => {
          const targetId = resolvedIds[section.id];
          return targetId ? { ...section, targetId } : null;
        })
        .filter(
          (
            section,
          ): section is CoursePageSection & {
            targetId: string;
          } => section !== null,
        ),
    [resolvedIds, sectionSignature],
  );
}

function useActiveCourseSection(
  resolvedSections: Array<CoursePageSection & { targetId: string }>,
) {
  const [activeId, setActiveId] = useState<string | null>(
    resolvedSections[0]?.id ?? null,
  );

  useEffect(() => {
    if (resolvedSections.length === 0) {
      setActiveId(null);
      return;
    }

    const hashId = window.location.hash.slice(1);
    const hashSection = resolvedSections.find(
      (section) => section.targetId === hashId || section.id === hashId,
    );
    setActiveId(hashSection?.id ?? resolvedSections[0]!.id);

    const visibleTargets = new Set<string>();

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            visibleTargets.add(entry.target.id);
          } else {
            visibleTargets.delete(entry.target.id);
          }
        }

        const next = resolvedSections
          .filter((section) => visibleTargets.has(section.targetId))
          .map((section) => {
            const element = document.getElementById(section.targetId);
            return element
              ? {
                  id: section.id,
                  distance: Math.abs(element.getBoundingClientRect().top - 112),
                }
              : null;
          })
          .filter(
            (candidate): candidate is { id: string; distance: number } =>
              candidate !== null,
          )
          .sort((a, b) => a.distance - b.distance)[0];

        if (next) setActiveId(next.id);
      },
      {
        root: null,
        rootMargin: SECTION_OBSERVER_ROOT_MARGIN,
        threshold: [0, 0.01, 0.5],
      },
    );

    for (const section of resolvedSections) {
      const target = document.getElementById(section.targetId);
      if (target) observer.observe(target);
    }

    return () => observer.disconnect();
  }, [resolvedSections]);

  return activeId;
}

function scrollToSection(targetId: string) {
  const target = document.getElementById(targetId);
  if (!target) return;
  // `SITE-CRS02`: a band never rendered has a stand-in height; render them first.
  renderBelowFoldBands();

  const reduceMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)",
  ).matches;
  target.scrollIntoView({
    behavior: reduceMotion ? "auto" : "smooth",
    block: "start",
  });
  window.history.replaceState(null, "", `#${targetId}`);
}

function SectionLinks({
  sections,
  activeId,
  onNavigate,
  className,
  ariaLabel,
  tabbable = true,
}: {
  sections: Array<CoursePageSection & { targetId: string }>;
  activeId: string | null;
  onNavigate?: () => void;
  className?: string;
  ariaLabel: string;
  tabbable?: boolean;
}) {
  return (
    <nav aria-label={ariaLabel} className={className}>
      <ul className="space-y-1">
        {sections.map((section, index) => {
          const isActive = activeId === section.id;
          return (
            <li key={section.id}>
              <a
                href={`#${section.targetId}`}
                aria-current={isActive ? "location" : undefined}
                tabIndex={tabbable ? undefined : -1}
                onClick={(event) => {
                  event.preventDefault();
                  scrollToSection(section.targetId);
                  onNavigate?.();
                }}
                className={cn(
                  "group flex min-h-11 items-center gap-3 rounded-[10px] px-3 py-2.5 text-sm transition-colors duration-150 ease-out",
                  isActive
                    ? "bg-[#FFF1EC] font-semibold text-neutral-950"
                    : "font-medium text-neutral-600 hover:bg-neutral-50 hover:text-neutral-950",
                )}
              >
                <span
                  className={cn(
                    "flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold transition-colors duration-150",
                    isActive
                      ? "bg-primary text-white"
                      : "bg-neutral-100 text-neutral-500 group-hover:bg-neutral-200",
                  )}
                  aria-hidden
                >
                  {String(index + 1).padStart(2, "0")}
                </span>
                <span className="min-w-0 flex-1 truncate">{section.label}</span>
                {isActive ? (
                  <span
                    className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary"
                    aria-hidden
                  />
                ) : null}
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

export function CourseDesktopSectionPopover({
  sections,
  tone = "light",
  placement = "down",
}: NavigatorProps) {
  const t = useTranslations("courseDetail.sectionNav");
  const resolvedSections = useResolvedSections(sections);
  const activeId = useActiveCourseSection(resolvedSections);
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const activeSection =
    resolvedSections.find((section) => section.id === activeId) ??
    resolvedSections[0];
  const activeIndex = activeSection
    ? resolvedSections.findIndex((section) => section.id === activeSection.id) + 1
    : 0;

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (
        !panelRef.current?.contains(target) &&
        !triggerRef.current?.contains(target)
      ) {
        setOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setOpen(false);
      triggerRef.current?.focus();
    };

    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  if (resolvedSections.length === 0) return null;

  return (
    <div className="relative shrink-0">
      <Button
        ref={triggerRef}
        type="button"
        variant={tone === "dark" ? "outlineDark" : "outline"}
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((value) => !value)}
        className="group h-11 max-w-[15rem] justify-start gap-2.5 rounded-[10px] px-3 text-left"
      >
        <span
          className={cn(
            "flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-primary",
            tone === "dark" ? "bg-white/10" : "bg-white shadow-sm",
          )}
        >
          <ListTree className="h-4 w-4" aria-hidden />
        </span>
        <span className="hidden min-w-0 flex-1 xl:block">
          <span
            className={cn(
              "block text-[10px] font-semibold uppercase tracking-[0.14em]",
              tone === "dark" ? "text-neutral-400" : "text-neutral-500",
            )}
          >
            {t("eyebrow")}
          </span>
          <span
            className={cn(
              "block truncate text-[13px] font-semibold",
              tone === "dark" ? "text-white" : "text-neutral-950",
            )}
          >
            {activeSection?.label}
          </span>
        </span>
        <span
          className={cn(
            "text-[13px] font-semibold xl:hidden",
            tone === "dark" ? "text-white" : "text-neutral-800",
          )}
        >
          {t("trigger")}
        </span>
        <ChevronDown
          className={cn(
            "h-4 w-4 shrink-0 text-neutral-500 transition-transform duration-200 ease-out",
            (placement === "up" ? !open : open) && "rotate-180",
          )}
          aria-hidden
        />
      </Button>

      <div
        ref={panelRef}
        id={panelId}
        aria-hidden={!open}
        className={cn(
          "absolute z-50 w-[min(21rem,calc(100vw-3rem))] overflow-hidden rounded-xl border border-neutral-200 bg-white text-neutral-950 shadow-[0_16px_40px_rgba(17,17,17,0.12)] transition duration-200 ease-out",
          placement === "up"
            ? "bottom-full right-0 mb-3 origin-bottom-right"
            : "left-0 top-full mt-3 origin-top-left",
          open
            ? "visible translate-y-0 scale-100 opacity-100"
            : cn("invisible scale-[0.98] opacity-0", placement === "up" ? "translate-y-1" : "-translate-y-1"),
        )}
      >
        <div className="border-b border-neutral-100 px-4 py-3">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-primary">
                {t("eyebrow")}
              </p>
              <p className="mt-0.5 text-sm font-semibold text-neutral-950">
                {t("jumpHint")}
              </p>
            </div>
            <span className="rounded-full bg-neutral-100 px-2.5 py-1 text-[11px] font-semibold text-neutral-600">
              {String(activeIndex).padStart(2, "0")} /{" "}
              {String(resolvedSections.length).padStart(2, "0")}
            </span>
          </div>
        </div>
        <SectionLinks
          sections={resolvedSections}
          activeId={activeId}
          onNavigate={() => setOpen(false)}
          ariaLabel={t("navAriaLabel")}
          tabbable={open}
          className="max-h-[min(60vh,32rem)] overflow-y-auto p-2"
        />
      </div>
    </div>
  );
}

export function CourseMobileSectionSheet({ sections, tone = "light" }: NavigatorProps) {
  const t = useTranslations("courseDetail.sectionNav");
  const resolvedSections = useResolvedSections(sections);
  const activeId = useActiveCourseSection(resolvedSections);
  const [open, setOpen] = useState(false);
  const dialogId = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  const close = useCallback(() => {
    setOpen(false);
    window.setTimeout(() => triggerRef.current?.focus(), 0);
  }, []);

  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeRef.current?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        close();
        return;
      }
      if (event.key !== "Tab") return;

      const focusable = Array.from(
        dialogRef.current?.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ) ?? [],
      );
      const first = focusable[0];
      const last = focusable.at(-1);
      if (!first || !last) return;

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [close, open]);

  if (resolvedSections.length === 0) return null;

  const activeSection =
    resolvedSections.find((section) => section.id === activeId) ??
    resolvedSections[0];

  return (
    <>
      <Button
        ref={triggerRef}
        type="button"
        variant={tone === "dark" ? "outlineDark" : "outline"}
        size="icon"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={dialogId}
        aria-label={t("open")}
        onClick={() => setOpen(true)}
        className="shrink-0 rounded-[10px] text-primary"
      >
        <ListTree className="h-[18px] w-[18px]" aria-hidden />
      </Button>

      <div
        className={cn(
          "fixed inset-0 z-[70] lg:hidden",
          open ? "pointer-events-auto" : "pointer-events-none",
        )}
        aria-hidden={!open}
      >
        <button
          type="button"
          aria-label={t("close")}
          tabIndex={open ? 0 : -1}
          onClick={close}
          className={cn(
            "absolute inset-0 bg-neutral-950/35 backdrop-blur-[2px] transition-opacity duration-200 ease-out",
            open ? "opacity-100" : "opacity-0",
          )}
        />
        <div
          ref={dialogRef}
          id={dialogId}
          role="dialog"
          aria-modal="true"
          aria-labelledby={`${dialogId}-title`}
          className={cn(
            "absolute inset-x-0 bottom-0 flex max-h-[78dvh] flex-col rounded-t-2xl border-t border-neutral-200 bg-white shadow-[0_-20px_50px_rgba(17,17,17,0.16)] transition-transform duration-200 ease-out",
            open ? "translate-y-0" : "translate-y-full",
          )}
          style={{
            paddingBottom: "env(safe-area-inset-bottom, 0px)",
          }}
        >
          <div className="flex items-start justify-between gap-4 border-b border-neutral-100 px-6 py-4">
            <div className="min-w-0">
              <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-primary">
                {t("eyebrow")}
              </p>
              <h2
                id={`${dialogId}-title`}
                className="mt-1 text-lg font-semibold text-neutral-950"
              >
                {t("title")}
              </h2>
              <p className="mt-1 truncate text-sm text-neutral-500">
                {t("current", { section: activeSection?.label ?? "" })}
              </p>
            </div>
            <button
              ref={closeRef}
              type="button"
              aria-label={t("close")}
              tabIndex={open ? 0 : -1}
              onClick={close}
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[10px] border border-neutral-200 text-neutral-600 outline-none transition-colors hover:bg-neutral-50 focus-visible:ring-2 focus-visible:ring-primary/30"
            >
              <X className="h-5 w-5" aria-hidden />
            </button>
          </div>
          <SectionLinks
            sections={resolvedSections}
            activeId={activeId}
            onNavigate={close}
            ariaLabel={t("navAriaLabel")}
            tabbable={open}
            className="overflow-y-auto px-4 py-3"
          />
        </div>
      </div>
    </>
  );
}
